import EventTarget from '@ungap/event-target';
import Debug from 'debug';
import EnvironmentHelper from '../EnvironmentHelper/EnvironmentHelper';
import { cloneEvent, newFeedItemEvent } from './events';
import SocketManager, { eventTypes as allSocketEventTypes } from './SocketManager';
import type {
  CommentFeedItem,
  CommentLivefireItem,
  EmailFeedItem,
  EmailLivefireItem,
  FeedItem,
  LikeFeedItem,
  LikeLivefireItem,
  LivefireApiOptions,
  LivefireItem,
  ReplyWithVideoFeedItem,
  ReplyWithVideoLivefireItem,
  VideoWatchedFeedItem,
  VideoWatchedLivefireItem,
} from './types';
import { ApiCallOptions, apiCall, arrayUtilities } from '@/functions';
import { LivefireCardType, livefireItemTypes, validateStringNotEmpty } from '@/constants';
import { EMAIL_TYPES, EmailLivefireCardType } from '@/constants/LivefireItemTypes';
import { NonUndefined } from '@/functions/typeHelpers';

const { click, open, play, videoWatched, like, comment, replyWithVideo } = livefireItemTypes;
const debug = Debug('livefire:api'); // eslint-disable-line new-cap

const socketEventTypes = allSocketEventTypes.filter((event) => event !== 'newItem');
const eventTypes = ['error', 'newFeedItem', 'reconnect', ...socketEventTypes] as const;

export type LivefireEvent = (typeof eventTypes)[number];

function sortFeedItem(first: FeedItem, second: FeedItem) {
  if (!first.eventTS) {
    return 1;
  }
  if (!second.eventTS) {
    return -1;
  }
  return -(first.eventTS - second.eventTS);
}

/**
 * @name LivefireApi
 */
class LivefireApi {
  emitter = new EventTarget();

  protected feeds = ['default'];

  protected apiCardTypes: LivefireCardType[] = [];

  protected apiClientId: string | null = null;

  socket: SocketManager | null = null;

  protected apiToken: string | null = null;

  constructor({
    cardTypes = [],
    clientId = null,
    token = null,
    feeds = ['default'],
  }: LivefireApiOptions = {}) {
    this.clientId = clientId;
    this.token = token;
    this.feeds = feeds;

    this.setCardTypes(cardTypes);
  }

  get cardTypes() {
    return this.apiCardTypes;
  }

  get clientId() {
    return this.apiClientId || null;
  }

  set clientId(value: string | null) {
    if (value !== null) {
      validateStringNotEmpty(value, 'clientId');
    }
    this.apiClientId = value;
  }

  get token() {
    return this.apiToken || null;
  }

  set token(value: string | null) {
    if (value !== null) {
      validateStringNotEmpty(value, 'token');
    }
    this.apiToken = value;
  }

  /**
   * this helps expose the business logic for verification and unit testing.
   * @type {URL}
   */
  // eslint-disable-next-line class-methods-use-this
  get url() {
    const { livefire } = EnvironmentHelper.getEnvironmentConfig();
    return new URL(livefire.baseUrl);
  }

  addEventListener(
    type: (typeof eventTypes)[number],
    listener: Parameters<EventTarget['addEventListener']>[1],
  ) {
    if (eventTypes.indexOf(type) === -1) {
      throw new Error(`Unrecognized event type name: ${type}`);
    }

    debug('addEventListener', listener);
    this.emitter.addEventListener(type, listener, { passive: true });
  }

  connect() {
    if (this.socket) {
      this.socket.connect();
      return;
    }

    const socket = new SocketManager({
      cardTypes: this.cardTypes,
      clientId: this.clientId,
      token: this.token,
      url: this.url,
      feeds: this.feeds,
    });

    socketEventTypes.forEach((type) => {
      socket.addEventListener(type, (ev) => {
        debug(type, ev);
        this.emitter.dispatchEvent(cloneEvent(ev as CustomEvent));
      });
    });

    socket.addEventListener('newItem', this.onNewItem.bind(this));

    socket.connect();

    this.socket = socket;
  }

  disconnect() {
    if (!this.socket) {
      return;
    }

    this.socket.disconnect();
  }

  createEmailFeedItemFromLivefireItemByType(
    item: EmailLivefireItem,
    livefireItemType: EmailLivefireCardType,
  ): EmailFeedItem {
    validateStringNotEmpty(livefireItemType, 'livefireItemType');

    const feedItem: EmailFeedItem = {
      eventTS: item.sendDate,
      id: item.id,
      muted: !!item.muted,
      recipientName: item.contactName || item.recipientName || 'Unknown',
      recipients: item.recipients,
      subject: item.subject || 'No Subject',
      sentTS: item.sendDate,
      type: livefireItemType,
      _original: item,
    };

    const result = this.getInteractionDetailsFromEmailLivefireItem(item, livefireItemType);
    if (result) {
      const [count, eventTS] = result;
      feedItem.count = count;
      feedItem.eventTS = eventTS;
    }

    return feedItem;
  }

  // eslint-disable-next-line class-methods-use-this
  getInteractionDetailsFromEmailLivefireItem(
    item: EmailLivefireItem,
    itemType: LivefireCardType,
  ): [number, number] | null {
    if (itemType === open && item.opens && item.latestOpenTimestamp) {
      return [item.opens, item.latestOpenTimestamp];
    }
    if (itemType === play && item.plays && item.latestPlayTimestamp) {
      return [item.plays, item.latestPlayTimestamp];
    }
    if (itemType === click && item.clicks && item.latestClickTimestamp) {
      return [item.clicks, item.latestClickTimestamp];
    }
    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  createVideoWatchedFeedItemFromLivefireItem(
    livefireItem: VideoWatchedLivefireItem,
  ): VideoWatchedFeedItem {
    if (!livefireItem) {
      throw new Error('Cannot create a video watched feed item without a livefireItem');
    }

    const feedItem: VideoWatchedFeedItem = {
      count: parseFloat(`${livefireItem.totalViews}`),
      eventTS: livefireItem.updated_timestamp,
      id: livefireItem.id,
      videoId: livefireItem.videoId,
      muted: !!livefireItem.muted,
      recipientName: livefireItem.emailAddress || 'Someone',
      recipients: livefireItem.recipients,
      percentWatched: livefireItem.videoWatchedPercent,
      maxVideoWatchedPercent: livefireItem.maxVideoWatchedPercent,
      sentTS: livefireItem.viewDate,
      subject: livefireItem.videoName,
      type: videoWatched,
      _original: livefireItem,
    };

    return feedItem;
  }

  // eslint-disable-next-line class-methods-use-this
  createCommentFeedItemFromLivefireItem(livefireItem: CommentLivefireItem): CommentFeedItem {
    if (!livefireItem) {
      throw new Error('Cannot create a comment feed item without a livefireItem');
    }

    const feedItem: CommentFeedItem = {
      count: livefireItem.totalLikes,
      eventTS: livefireItem.updated_timestamp,
      sentTS: livefireItem.interactionDate,
      id: livefireItem.id,
      muted: !!livefireItem.muted,
      recipientName: livefireItem.emailAddress || 'Someone',
      subject: livefireItem.videoName,
      commentMessage: livefireItem.message,
      videoId: livefireItem.videoId,
      type: comment,
      _original: livefireItem,
    };

    return feedItem;
  }

  // eslint-disable-next-line class-methods-use-this
  createReplyWithVideoFeedItemFromLivefireItem(
    livefireItem: ReplyWithVideoLivefireItem,
  ): ReplyWithVideoFeedItem {
    if (!livefireItem) {
      throw new Error('Cannot create a reply with video feed item without a livefireItem');
    }

    const feedItem: ReplyWithVideoFeedItem = {
      eventTS: livefireItem.updated_timestamp,
      sentTS: livefireItem.interactionDate,
      id: livefireItem.id,
      subject: livefireItem.videoName,
      muted: !!livefireItem.muted,
      recipientName: livefireItem.replierEmail,
      replyThumbnail: livefireItem.replyThumbnail,
      replyVideoId: livefireItem.replyVideoId,
      replyVideoName: livefireItem.replyVideoName,
      replyVideoUrl: livefireItem.replyVideoUrl,
      commentMessage: livefireItem.message,
      videoId: livefireItem.videoId,
      type: replyWithVideo,
      _original: livefireItem,
    };

    return feedItem;
  }

  // eslint-disable-next-line class-methods-use-this
  createLikeFeedItemFromLivefireItem(livefireItem: LikeLivefireItem): LikeFeedItem {
    if (!livefireItem) {
      throw new Error('Cannot create a like feed item without a livefireItem');
    }

    const feedItem: LikeFeedItem = {
      subject: livefireItem.videoName,
      eventTS: livefireItem.updated_timestamp,
      sentTS: livefireItem.interactionDate,
      muted: !!livefireItem.muted,
      count: livefireItem.totalLikes,
      id: livefireItem.id,
      videoId: livefireItem.videoId,
      recipientName: livefireItem.emailAddress || 'Someone',
      type: like,
      _original: livefireItem,
    };

    return feedItem;
  }

  enumerateFeedItemsFromLivefireItem(livefireItem: LivefireItem | null): FeedItem[] {
    if (!livefireItem) {
      return [];
    }

    const feedItems: FeedItem[] = [];

    if (this.isAnEmailLivefireItem(livefireItem)) {
      let addSentItem = true;

      [open, click, play].forEach((interaction) => {
        const result = this.getInteractionDetailsFromEmailLivefireItem(livefireItem, interaction);
        if (result) {
          feedItems.push(this.createEmailFeedItemFromLivefireItemByType(livefireItem, interaction));
          addSentItem = false;
        }
      });

      if (addSentItem) {
        feedItems.push(this.createEmailFeedItemFromLivefireItemByType(livefireItem, 'sent'));
      }
    } else if (this.itemIsAVideoWatchedLivefireItem(livefireItem)) {
      const feedItem = this.createVideoWatchedFeedItemFromLivefireItem(livefireItem);
      feedItems.push(feedItem);
    } else if (this.itemIsALikeLivefireItem(livefireItem)) {
      const feedItem = this.createLikeFeedItemFromLivefireItem(livefireItem);
      feedItems.push(feedItem);
    } else if (this.itemIsACommentLivefireItem(livefireItem)) {
      const feedItem = this.createCommentFeedItemFromLivefireItem(livefireItem);
      feedItems.push(feedItem);
    } else if (this.itemIsAReplyWithVideoLivefireItem(livefireItem)) {
      const feedItem = this.createReplyWithVideoFeedItemFromLivefireItem(livefireItem);
      feedItems.push(feedItem);
    }

    return feedItems;
  }

  enumerateFeedItemsFromLivefireItems(livefireItems: LivefireItem[]): FeedItem[] {
    if (!Array.isArray(livefireItems)) {
      return [];
    }

    const feedItems: FeedItem[] = [];
    livefireItems.forEach((item) => {
      const enumeratedFeedItems = this.enumerateFeedItemsFromLivefireItem(item);
      feedItems.push(...enumeratedFeedItems);
    });

    return feedItems;
  }

  async getFeedItems() {
    try {
      if (!this.token) {
        throw new Error('No token has been set');
      }

      const url = new URL(`list/${this.clientId}`, this.url);

      if (this.cardTypes.length) {
        // EX-3605: we need to request all item types and filter them out later for ... reasons.
        url.searchParams.append('types', livefireItemTypes.items.join(','));
      }

      return this.requestAndEnumerateFeedItems(url, this.token, this.cardTypes);
    } catch (error) {
      return {
        ok: false,
        error,
      };
    }
  }

  async getMutedFeedItems() {
    try {
      if (!this.token) {
        throw new Error('No token has been set');
      }

      const url = new URL(`list/${this.clientId}/muted`, this.url);

      if (this.cardTypes.length) {
        // EX-3605: we need to request all item types and filter them out later for ... reasons.
        url.searchParams.append('types', livefireItemTypes.items.join(','));
      }

      return this.requestAndEnumerateFeedItems(url, this.token, this.cardTypes);
    } catch (error) {
      return {
        ok: false,
        error,
      };
    }
  }

  // eslint-disable-next-line class-methods-use-this
  isAnEmailLivefireItem(livefireItem: unknown): livefireItem is EmailLivefireItem {
    if (livefireItem && typeof livefireItem === 'object' && 'itemType' in livefireItem) {
      return EMAIL_TYPES.includes(livefireItem.itemType as EmailLivefireCardType);
    }
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  itemIsACommentLivefireItem(item: unknown): item is CommentLivefireItem {
    if (item && typeof item === 'object' && 'itemType' in item) {
      return item.itemType === comment;
    }
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  itemIsAReplyWithVideoLivefireItem(item: unknown): item is ReplyWithVideoLivefireItem {
    if (item && typeof item === 'object' && 'itemType' in item) {
      return item.itemType === replyWithVideo;
    }
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  itemIsAVideoWatchedLivefireItem(item: unknown): item is VideoWatchedLivefireItem {
    if (item && typeof item === 'object' && 'itemType' in item) {
      return item.itemType === videoWatched;
    }
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  itemIsALikeLivefireItem(item: unknown): item is LikeLivefireItem {
    if (item && typeof item === 'object' && 'itemType' in item) {
      return item.itemType === like;
    }
    return false;
  }

  onNewItem(evt: Event) {
    const { item } = (evt as CustomEvent).detail;
    const itemType = item.itemType || item.type;

    // ignore the item if the type is not in the populated type list.
    if (this.cardTypes.length && !this.cardTypes.includes(itemType)) {
      return;
    }

    let feedItem = null;
    // handle cases where our feedItem has already gone through one of the create methods...
    if (typeof item.type !== 'undefined') {
      feedItem = item;
    } else if (this.isAnEmailLivefireItem(item)) {
      feedItem = this.createEmailFeedItemFromLivefireItemByType(item, itemType);
    } else if (this.itemIsAVideoWatchedLivefireItem(item)) {
      feedItem = this.createVideoWatchedFeedItemFromLivefireItem(item);
    } else if (this.itemIsALikeLivefireItem(item)) {
      feedItem = this.createLikeFeedItemFromLivefireItem(item);
    } else if (this.itemIsACommentLivefireItem(item)) {
      feedItem = this.createCommentFeedItemFromLivefireItem(item);
    } else if (this.itemIsAReplyWithVideoLivefireItem(item)) {
      feedItem = this.createReplyWithVideoFeedItemFromLivefireItem(item);
    } else {
      console.warn('Unrecognized item type. Dropping item.', item);
      return;
    }

    const newEvt = newFeedItemEvent(feedItem);
    this.emitter.dispatchEvent(newEvt);
  }

  reconnect({ clientId, token }: { clientId: string; token: string }) {
    this.disconnect();

    this.clientId = clientId;
    this.token = token;

    this.connect();
  }

  removeEventListener(
    type: (typeof eventTypes)[number],
    listener: Parameters<EventTarget['removeEventListener']>[1],
  ) {
    if (eventTypes.indexOf(type) === -1) {
      throw new Error(`Unrecognized event type name: ${type}`);
    }

    debug('removeEventListener', listener);
    this.emitter.removeEventListener(type, listener);
  }

  async requestAndEnumerateFeedItems(
    url: Parameters<typeof apiCall>[0],
    token: NonUndefined<ApiCallOptions['token']>,
    cardTypes: LivefireCardType[],
  ) {
    const result = await apiCall(url, { token, timeout: 5000 });
    if (!result.ok) {
      return result;
    }

    const feedItems = this.enumerateFeedItemsFromLivefireItems(
      result.data as unknown as LivefireItem[],
    ).filter((fi) => cardTypes.length === 0 || cardTypes.includes(fi.type));

    feedItems.sort(sortFeedItem);

    return {
      ok: true,
      feedItems,
    };
  }

  reset() {
    if (!this.socket) {
      return;
    }

    this.socket.reset();
  }

  setCardTypes(cardTypes: typeof this.apiCardTypes) {
    if (!Array.isArray(cardTypes)) {
      throw new Error('Must pass an array to setCardTypes');
    }

    const areCardTypesValid =
      cardTypes.length &&
      !arrayUtilities.areAllItemsInCollection(cardTypes, [...livefireItemTypes.items]);

    if (areCardTypesValid) {
      throw new Error('cardTypes must be an array of types from LivefireItemTypes');
    }

    const differences = arrayUtilities.diffArray(cardTypes, this.cardTypes);

    if (differences.length) {
      this.apiCardTypes = [...cardTypes];
      if (this.socket) {
        this.socket.setCardTypes(this.cardTypes);
      }
    }
  }
}

export default LivefireApi;
